<script setup lang="ts">
import { computed, defineAsyncComponent, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { settingTabs } from "@/config/settings/settingTabs";

const router = useRouter();
const route = useRoute("setting-tab");
const { t, locale } = useI18n({ useScope: "global" });

const drawer = ref(false);
const rail = ref(true);

definePage({
  meta: {
    navActiveLink: "setting-tab-module-settings",
    key: "tab",
  },
});

const componentMap = Object.fromEntries(
  settingTabs.map(item => [item.tab, defineAsyncComponent(item.loader)]),
) as Record<string, ReturnType<typeof defineAsyncComponent>>;

const tabs = computed(() =>
  settingTabs.map(item => ({
    ...item,
    title: t(item.titleKey),
  })),
);

// Active tab linked to route
const activeTab = computed({
  get: () => route.params.tab,
  set: (tab) => router.push({ name: "setting-tab", params: { tab } }),
});

// 🔥 Computed title based on active tab
const currentTitle = computed(() => {
  const tab = route.params.tab;
  const matched = tabs.value.find((t) => t.tab === tab);
  return matched ? matched.title : "";
});

const currentComponent = computed(() => {
  const currentTab = String(route.params.tab || settingTabs[0]?.tab || "");

  return componentMap[currentTab] ?? componentMap[settingTabs[0]?.tab || ""];
});
</script>

<template>
  <div class="setting-page-shell">
    <v-layout class="setting-page-shell__layout">
      <v-app-bar elevation="0" class="setting-page-shell__bar">
        <v-app-bar-nav-icon
          variant="text"
          color="secondary"
          @click.stop="drawer = !drawer"
        />
        <v-toolbar-title>{{ currentTitle }}</v-toolbar-title>
      </v-app-bar>

      <v-navigation-drawer
        class="scroll-y setting-page-shell__drawer"
        v-model="drawer"
        @click="rail = false"
        style="max-height: calc(100dvh / var(--alkokh-page-zoom, 1) - 64px); overflow-y: auto"
      >
        <VTabs
          v-model="activeTab"
          class="v-tabs-pill my-2"
          direction="vertical"
          style="padding-bottom: 16px"
        >
          <VTab
            v-for="item in tabs"
            :key="item.tab"
            :value="item.tab"
            :to="{ name: 'setting-tab', params: { tab: item.tab } }"
          >
            <VIcon size="20" start :icon="item.icon" />
            {{ item.title }}
          </VTab>
        </VTabs>
      </v-navigation-drawer>

      <v-main class="setting-page-shell__main">
        <component
          :is="currentComponent"
          :key="`${activeTab}-${locale}`"
        />
      </v-main>
    </v-layout>
  </div>
</template>

<style scoped>
.setting-page-shell,
.setting-page-shell__layout {
  background: transparent;
}

.setting-page-shell__bar,
.setting-page-shell__drawer {
  background: rgb(var(--v-theme-surface));
}

.setting-page-shell__main {
  min-block-size: 405px;
}

.v-navigation-drawer {
  overflow-y: auto !important;
}

.v-navigation-drawer .v-tabs {
  overflow-y: auto;
}
</style>
